import { supabase } from "@/integrations/supabase/client";

/**
 * Short-lived cache for the signed-in athlete's `clients` row.
 *
 * Widgets across the athlete app each looked this row up on mount
 * (`clients?select=id&user_id=eq…`), so a single screen issued five or more
 * identical round trips — and did it again on every tab switch. They now share
 * one in-flight request and its result for a few minutes, which is the single
 * biggest saving on slow mobile connections.
 */
export type CachedClientRow = {
  id: string;
  user_id: string;
  goal: string | null;
  plan: string | null;
  coach_id: string | null;
  weight: number | null;
  body_fat: number | null;
  comp_name: string | null;
  comp_date: string | null;
  created_at: string | null;
  timezone: string | null;
};

const TTL_MS = 5 * 60 * 1000;

let cache: {
  userId: string;
  fetchedAt: number;
  promise: Promise<CachedClientRow | null>;
} | null = null;

const SELECT =
  "id, user_id, goal, plan, coach_id, weight, body_fat, comp_name, comp_date, created_at, timezone";

/**
 * Fetches (or reuses) the athlete's client row. Never throws.
 * Pass `{ force: true }` to bypass the cache after something changed
 * (new weight, new show date, a plan just assigned).
 */
export function getClientRow(
  userId: string,
  opts?: { force?: boolean },
): Promise<CachedClientRow | null> {
  const fresh =
    !opts?.force &&
    cache && cache.userId === userId && Date.now() - cache.fetchedAt < TTL_MS;
  if (fresh) return cache!.promise;

  const promise = (async () => {
    const { data, error } = await supabase
      .from("clients")
      .select(SELECT)
      .eq("user_id", userId)
      .maybeSingle();
    if (error) {
      // Don't cache failures — the next caller should retry.
      if (cache?.promise === promise) cache = null;
      return null;
    }
    const row = (data as CachedClientRow | null) ?? null;
    // A missing profile is usually a brand-new athlete whose row is created
    // moments later — never hold on to that "nothing here" answer.
    if (!row && cache?.promise === promise) cache = null;
    return row;
  })();

  cache = { userId, fetchedAt: Date.now(), promise };
  return promise;
}

/** Drop the cached row (call after changing goal, weight, plan, coach, etc.). */
export function invalidateClientRow(): void {
  cache = null;
}
